import React, { useState } from 'react';
import toast from 'react-hot-toast';
import API from '../utils/api';
import Seo from '../components/Seo';

/* ─── PALETTE ─────────────────────────────────────────────── */
const V1 = '#7F00FF';
const V2 = '#B163FF';
const V3 = '#51158C';
const V4 = '#2B0057';

/* ─── SVG ICONS ───────────────────────────────────────────── */
const PhoneIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);
const MailIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);
const MapPinIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);
const ClockIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const ChevronDown = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const InstagramIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37a4 4 0 1 1-7.914 1.174A4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);
const LinkedInIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" />
  </svg>
);

/* ─── CIRCUIT BG ──────────────────────────────────────────── */
const CircuitBg = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0, opacity: 0.07 }}>
    <svg viewBox="0 0 1400 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
      <g stroke={V2} strokeWidth="0.9" fill="none">
        <line x1="0" y1="160" x2="320" y2="160" /><line x1="380" y1="160" x2="680" y2="160" />
        <line x1="980" y1="120" x2="1400" y2="120" />
        <line x1="0" y1="420" x2="210" y2="420" /><line x1="570" y1="420" x2="880" y2="420" />
        <line x1="0" y1="660" x2="460" y2="660" /><line x1="810" y1="660" x2="1400" y2="660" />
        <circle cx="320" cy="160" r="5" fill={V1} opacity="0.9" />
        <circle cx="680" cy="160" r="4" fill={V2} opacity="0.9" />
        <circle cx="210" cy="420" r="4" fill={V2} opacity="0.9" />
      </g>
    </svg>
  </div>
);

/* ─── CONTACT INFO DATA ───────────────────────────────────── */
const contactInfo = [
  {
    Icon: PhoneIcon,
    title: 'Phone',
    value: '+91 6381253611',
    href: 'tel:+91 6381253611',
    desc: 'Mon–Sat, 9 AM – 6 PM IST',
  },
  {
    Icon: MailIcon,
    title: 'Email',
    value: 'support@thiranx.in',
    href: 'mailto:support@thiranx.in',
    desc: 'We reply within 24 hours',
  },
  {
    Icon: MapPinIcon,
    title: 'Location',
    value: 'Tamil Nadu, India',
    href: null,
    desc: 'Remote-first organisation',
  },
  {
    Icon: ClockIcon,
    title: 'Working Hours',
    value: 'Mon – Sat, 9 AM – 6 PM',
    href: null,
    desc: 'IST (UTC +5:30)',
  },
  {
    Icon: InstagramIcon,
    title: 'Instagram',
    value: '@thiranx_2026',
    href: 'https://www.instagram.com/thiranx_2026?igsh=Y3Q0dG4ycmxpc2x5',
    desc: 'Follow us for updates',
  },
  {
    Icon: LinkedInIcon,
    title: 'LinkedIn',
    value: 'ThiranX Institute',
    href: 'https://www.linkedin.com/company/thiranx/',
    desc: 'Connect with us professionally',
  },
];

/* ─── FAQ DATA ────────────────────────────────────────────── */
const faqs = [
  { q: 'How do I enrol in a course?', a: 'Simply create a free account, browse our courses, and click "Apply Now" on the course you\'re interested in. Our team will review your application and get back to you within 24 hours.' },
  { q: 'Are the certificates valid for placements?', a: 'Yes! ThiranX is MSME-certified and all our certificates are government-recognised. They carry weight for placements, higher education, and professional profiles.' },
  { q: 'What payment methods do you accept?', a: 'We accept UPI, Debit/Credit Cards, Net Banking, and popular wallets. EMI options are also available for select courses.' },
  { q: 'Do you offer internship opportunities?', a: 'Yes! We have a dedicated internship programme where students work on real projects with industry mentors and earn a verifiable certificate upon completion.' },
];

/* ─── FAQ ITEM COMPONENT ──────────────────────────────────── */
const FaqItem = ({ q, a, isOpen, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: 'var(--card-solid)',
      border: `1.5px solid ${isOpen ? V1 + '66' : 'var(--border)'}`,
      borderRadius: 16, padding: '20px 24px',
      cursor: 'pointer',
      transition: 'all 0.25s ease',
      boxShadow: isOpen ? `0 8px 32px ${V1}18` : 'none',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
      <h4 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', lineHeight: 1.4 }}>{q}</h4>
      <span style={{
        color: V2, flexShrink: 0,
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.25s ease',
      }}>
        <ChevronDown />
      </span>
    </div>
    {isOpen && (
      <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.7, marginTop: 12, paddingRight: 32 }}>
        {a}
      </p>
    )}
  </div>
);

/* ─── MAIN COMPONENT ──────────────────────────────────────── */
const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSending(true);
    try {
      await API.post('/contact', form);
      toast.success('Message sent! We\'ll get back to you soon 🎉');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to send. Please try again.';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '14px 18px',
    background: 'var(--bg3)', border: '1.5px solid var(--border)',
    borderRadius: 12, color: 'var(--text)', fontSize: 14,
    fontFamily: "'Satoshi','DM Sans',sans-serif", fontWeight: 500,
    outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <Seo
        title="Contact Us"
        description="Get in touch with ThiranX for questions about courses, internships, or micro-internships. Reach us by phone, email, or the contact form."
        path="/contact"
      />

      {/* ══ HERO ════════════════════════════════════════════ */}
      <section style={{
        minHeight: '50vh', display: 'flex', alignItems: 'center',
        position: 'relative', overflow: 'hidden',
        padding: '140px 0 80px', background: 'var(--bg)',
      }}>
        <CircuitBg />
        <div style={{ position: 'absolute', top: '20%', right: '-5%', width: 400, height: 400, borderRadius: '50%', background: `radial-gradient(circle, var(--hero-orb) 0%, transparent 70%)`, pointerEvents: 'none' }} />

        <div className="container" style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            border: '1px solid var(--badge-pill-border)', borderRadius: 999,
            padding: '6px 18px', marginBottom: 36,
            fontSize: 11.5, fontWeight: 800, color: 'var(--badge-pill-text)',
            letterSpacing: '0.08em', textTransform: 'uppercase',
            background: 'var(--badge-pill-bg)',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: V2 }} />
            Get In Touch
          </div>

          <h1 style={{
            fontSize: 'clamp(38px,6vw,76px)',
            fontWeight: 800, lineHeight: 1.06,
            letterSpacing: '-0.03em', marginBottom: 20,
            fontFamily: "'Inter',sans-serif",
          }}>
            Contact{' '}
            <span style={{
              background: `linear-gradient(135deg,${V1},${V2})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>Us</span>
          </h1>

          <p style={{
            color: 'var(--text-2)', fontSize: 'clamp(15px,1.6vw,18px)',
            maxWidth: 500, lineHeight: 1.8, margin: '0 auto',
          }}>
            Have questions? We'd love to hear from you. Reach out and we'll respond as soon as we can.
          </p>
        </div>
      </section>

      {/* ══ CONTACT DETAILS + FORM ═════════════════════════ */}
      <section className="contact-unified-section" style={{ padding: '0 0 96px', background: 'var(--bg)' }}>
        <div className="container">
          <div className="contact-unified-panel">
            <div className="contact-details-panel">
              <div className="section-label">CONTACT DETAILS</div>
              <h2>Let’s start a conversation</h2>
              <p className="contact-details-intro">Choose the best way to reach us. Our team is happy to help.</p>
              <div className="contact-details-list">
                {contactInfo.map(({ Icon, title, value, href, desc }) => {
                  const content = (
                    <>
                      <span className="contact-detail-icon"><Icon /></span>
                      <span className="contact-detail-copy">
                        <span className="contact-detail-title">{title}</span>
                        <strong>{value}</strong>
                        <span className="contact-detail-desc">{desc}</span>
                      </span>
                    </>
                  );

                  return href ? (
                    <a
                      key={title}
                      className="contact-detail-row"
                      href={href}
                      target={href.startsWith('https://') ? '_blank' : undefined}
                      rel={href.startsWith('https://') ? 'noopener noreferrer' : undefined}
                    >
                      {content}
                    </a>
                  ) : (
                    <div key={title} className="contact-detail-row">
                      {content}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="contact-form-panel">
              <div className="section-label">SEND A MESSAGE</div>
              <h2>
                Send Us a{' '}
                <span style={{
                  background: `linear-gradient(135deg,${V1},${V2})`,
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                }}>Message</span>
              </h2>
              <p className="contact-form-intro">Fill out the form below and we'll get back to you within 24 hours.</p>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }} className="contact-form-grid">
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
                    Name <span style={{ color: '#f04155' }}>*</span>
                  </label>
                  <input
                    name="name" value={form.name} onChange={handleChange}
                    placeholder="Your full name"
                    style={inputStyle}
                    onFocus={e => { e.target.style.borderColor = V1; e.target.style.boxShadow = `0 0 0 3px ${V1}22`; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
                    Email <span style={{ color: '#f04155' }}>*</span>
                  </label>
                  <input
                    name="email" type="email" value={form.email} onChange={handleChange}
                    placeholder="you@example.com"
                    style={inputStyle}
                    onFocus={e => { e.target.style.borderColor = V1; e.target.style.boxShadow = `0 0 0 3px ${V1}22`; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Subject</label>
                <input
                  name="subject" value={form.subject} onChange={handleChange}
                  placeholder="What's this about?"
                  style={inputStyle}
                  onFocus={e => { e.target.style.borderColor = V1; e.target.style.boxShadow = `0 0 0 3px ${V1}22`; }}
                  onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
                  Message <span style={{ color: '#f04155' }}>*</span>
                </label>
                <textarea
                  name="message" value={form.message} onChange={handleChange}
                  placeholder="Tell us how we can help..."
                  rows={5}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
                  onFocus={e => { e.target.style.borderColor = V1; e.target.style.boxShadow = `0 0 0 3px ${V1}22`; }}
                  onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              <button
                type="submit" disabled={sending}
                className="btn btn-primary"
                style={{
                  width: '100%', padding: '16px 32px', fontSize: 15,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  opacity: sending ? 0.7 : 1,
                }}
              >
                {sending ? (
                  <>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2.5px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.7s linear infinite' }} />
                    Sending...
                  </>
                ) : (
                  <>
                    <SendIcon /> Send Message
                  </>
                )}
              </button>
            </form>
            </div>
          </div>
        </div>
      </section>

      {/* ══ FAQ SECTION ═════════════════════════════════════ */}
      <section style={{ padding: '100px 0', background: 'var(--bg)', position: 'relative', overflow: 'hidden' }}>
        <CircuitBg />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div className="section-label">FAQ</div>
            <h2 style={{ fontSize: 'clamp(28px,4vw,48px)', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 12 }}>
              Frequently Asked{' '}
              <span style={{
                background: `linear-gradient(135deg,${V1},${V2})`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
              }}>Questions</span>
            </h2>
            <p style={{ color: 'var(--muted)', fontSize: 16, maxWidth: 440, margin: '0 auto' }}>
              Quick answers to common questions
            </p>
          </div>

          <div style={{ maxWidth: 680, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {faqs.map((faq, i) => (
              <FaqItem
                key={i}
                q={faq.q}
                a={faq.a}
                isOpen={openFaq === i}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA BANNER ══════════════════════════════════════ */}
      <section style={{ padding: '80px 0', background: 'var(--bg2)' }}>
        <div className="container">
          <div style={{
            borderRadius: 28, position: 'relative', overflow: 'hidden',
            background: `linear-gradient(135deg,${V4} 0%,${V3} 50%,${V1} 100%)`,
            padding: 'clamp(48px,6vw,80px) clamp(28px,5vw,60px)',
            textAlign: 'center',
          }}>
            <CircuitBg />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h2 style={{
                fontSize: 'clamp(26px,4vw,44px)', fontWeight: 900, color: '#fff',
                marginBottom: 14, letterSpacing: '-0.025em',
                fontFamily: "'Inter',sans-serif",
              }}>
                Prefer a Quick Call?
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: 16, maxWidth: 400, margin: '0 auto 32px', lineHeight: 1.7 }}>
                Call us directly and talk to our team. We're happy to help!
              </p>
              <a
                href="tel:+916381253611"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  background: '#fff', color: V1, padding: '16px 40px', borderRadius: 12,
                  fontWeight: 800, fontSize: 16, textDecoration: 'none',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  transition: 'all 0.22s cubic-bezier(.34,1.56,.64,1)',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.2)'; }}
              >
                <PhoneIcon /> +91 63812 53611
              </a>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .contact-unified-panel {
          display: grid;
          grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
          overflow: hidden;
          border: 1px solid var(--border);
          border-radius: 24px;
          background: var(--card-solid);
          box-shadow: var(--shadow-md);
        }
        .contact-details-panel, .contact-form-panel { padding: clamp(26px, 4vw, 48px); }
        .contact-details-panel {
          position: relative;
          overflow: hidden;
          background: linear-gradient(145deg, var(--bg2), var(--bg3));
          border-right: 1px solid var(--border);
        }
        .contact-details-panel::after {
          position: absolute; right: -100px; bottom: -120px; width: 280px; height: 280px;
          border: 1px solid var(--border-strong); border-radius: 50%; content: "";
          box-shadow: 0 0 0 28px rgba(127,0,255,0.025), 0 0 0 58px rgba(127,0,255,0.02);
          pointer-events: none;
        }
        .contact-details-panel > *, .contact-form-panel > * { position: relative; z-index: 1; }
        .contact-details-panel h2, .contact-form-panel h2 {
          margin: 14px 0 8px; font-family: 'Inter', sans-serif;
          font-size: clamp(23px, 2.5vw, 32px); font-weight: 800;
          letter-spacing: -0.035em; line-height: 1.2;
        }
        .contact-details-intro, .contact-form-intro {
          margin: 0; color: var(--muted); font-size: 14px; line-height: 1.7;
        }
        .contact-details-list { display: grid; gap: 5px; margin-top: 28px; }
        .contact-detail-row {
          position: relative; display: flex; align-items: flex-start; gap: 13px;
          padding: 12px 10px; border-radius: 12px; color: inherit; text-decoration: none;
          transition: background 0.2s, transform 0.2s;
        }
        a.contact-detail-row:hover { background: rgba(127,0,255,0.07); transform: translateX(3px); }
        .contact-detail-icon {
          display: grid; width: 38px; height: 38px; flex: 0 0 38px; place-items: center;
          border: 1px solid var(--border-strong); border-radius: 11px;
          background: var(--card-solid); color: ${V2};
        }
        .contact-detail-icon svg { width: 18px; height: 18px; }
        .contact-detail-copy { display: grid; gap: 3px; min-width: 0; }
        .contact-detail-title { color: ${V2}; font-size: 10px; font-weight: 800; letter-spacing: .09em; text-transform: uppercase; }
        .contact-detail-copy strong { overflow-wrap: anywhere; color: var(--text); font-size: 13px; font-weight: 750; }
        .contact-detail-desc { color: var(--muted); font-size: 11.5px; line-height: 1.45; }
        .contact-form-intro { margin-bottom: 25px; }
        .contact-form-panel form { margin-top: 24px; }
        @media (max-width: 600px) {
          .contact-form-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 860px) {
          .contact-unified-panel { grid-template-columns: 1fr; }
          .contact-details-panel { border-right: 0; border-bottom: 1px solid var(--border); }
          .contact-details-list { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 4px; }
        }
        @media (max-width: 520px) {
          .contact-details-list { grid-template-columns: 1fr; }
          .contact-details-panel, .contact-form-panel { padding: 24px 20px; }
          .contact-unified-panel { border-radius: 18px; }
        }
      `}</style>
    </div>
  );
};

export default Contact;
